import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import BadgePopup from "../components/Badgepopup";
function IslamicTradePage25() {
  const [isOpen, setIsOpen] = useState(true);
  const links = {
    prev: "/islamic-trade-page-24",
    index: "/index",
    next: "/index-economics",
  };
  const contentRef = useRef(null);
  const titleText = "Rulings pertaining Grants (Hibah) and Donations(Atiyah)";
  const questions = [
    {
      text: "1. What is the main distinction between Hibah and Atiyah in Islamic law?",
      options: [
        {
          value: "A",
          label: "A. Hibah is revocable, Atiyah is not.",
        },
        {
          value: "B",
          label: "B. Hibah is given during illness, Atiyah during health.",
        },
        {
          value: "C",
          label:
            "C. Hibah is unrestricted, Atiyah is limited to one-third of the estate during fatal illness.",
        },
        {
          value: "D",
          label: "D. Both are subject to inheritance laws.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. When does a Hibah or Atiyah become legally binding and irrevocable (except in specific cases)?",
      options: [
        { value: "A", label: "A. When the giver announces it publicly" },
        { value: "B", label: "B. When the receiver verbally accepts" },
        {
          value: "C",
          label: "C. When the recipient takes full possession of the item",
        },
        { value: "D", label: "D. When the giver writes it in a will" },
      ],
      correct: "C",
    },
    {
      text: "3. Who is permitted in Islam to revoke a gift even after possession has been taken?",
      options: [
        { value: "A", label: "A. Any family member" },
        { value: "B", label: "B. A husband to his wife" },
        { value: "C", label: "C. A father from his child" },
        { value: "D", label: "D. An older sibling to a younger one" },
      ],
      correct: "C",
    },
    {
      text: "4. How should Hibah be distributed among children?",
      options: [
        { value: "A", label: "A. Equally among all children" },
        { value: "B", label: "B. In proportion to their financial needs" },
        { value: "C", label: "C. Based on their age" },
        {
          value: "D",
          label:
            "D. According to inheritance shares (a male receives double the share of a female)",
        },
      ],
      correct: "D",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={25}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 25</b> of our journey through Islamic financial
        jurisprudence. Today, we delve into the rulings related to Hibah
        (lifetime grants) and Atiyah (donations given in old age or during fatal
        illness), which are two important yet often misunderstood concepts in
        wealth transfer. These acts of giving are rooted in Islamic ethics of
        generosity and family responsibility, but they come with specific rules
        to ensure fairness, especially when it comes to preserving the rights of
        heirs. In this lesson, we will explore when such gifts are permissible,
        when they can be revoked, and how they must align with broader Shariah
        principles, particularly those governing inheritance and justice. In
        Islamic teachings, Hibah (lifetime grant) and Atiyah (donation in old
        age or fatal illness) are two forms of voluntary wealth transfer that
        are distinct from charity (Sadaqah) and gifts (Hadiyah). <br />
        Hibah refers to the act of giving something to another person during
        one’s lifetime, while the giver is in good health and full control of
        their faculties. It is an act of generosity aimed at benefiting the
        recipient without expecting anything in return.
        <br /> The Prophet Muhammad (peace be upon him) emphasized the
        permissibility and virtue of Hibah, stating,
        <br /> “Give gifts to one another, for it will increase love between
        you”
        <i>
          <a href="https://sunnah.com/adab:594" target="blank">
            (Al Adab Al Mufrad 594)
          </a>
        </i>
        . <br />
        Although this hadith primarily refers to Hadiyah (gifts), scholars have
        drawn parallels to Hibah, as both involve voluntary transfers that
        strengthen social bonds. <br />
        The Quran also encourages generosity, stating, “And do not forget to be
        gracious to one another. Indeed, Allah sees what you do”{" "}
        <i>
          <a href="https://quran.com/2/237" target="blank">
            {" "}
            (Surah Al-Baqarah 2:237)
          </a>
        </i>
        , which scholars interpret as encompassing all forms of lawful giving,
        including Hibah.
        <br /> Atiyah (donation in old age or fatal illness), on the other hand,
        refers to wealth given away by a person who has reached an advanced age
        or is suffering from an illness that might lead to death. Unlike Hibah,
        which is unrestricted, Atiyah is subject to Islamic inheritance laws and
        cannot exceed one-third of a person's estate unless approved by the
        legal heirs. This restriction exists because Atiyah is given at a stage
        when a person’s death is foreseeable, and without regulation, it could
        be used to bypass the rightful shares of inheritance. <br />
        The Prophet (peace be upon him) clarified this in the hadith of Sa’d ibn
        Abi Waqqas (may Allah be pleased with him), who wished to donate his
        entire wealth while suffering from an illness, but the Prophet limited
        it to one-third, saying, “One third, yet even one third is too much. It
        is better for you to leave your inheritors wealthy than to leave them
        poor begging others”
        <i>
          <a href="https://sunnah.com/bukhari:2742" target="blank">
            (Sahih al-Bukhari 2742)
          </a>
        </i>{" "}
        <br />
        This ruling ensures that Atiyah does not unjustly deprive rightful heirs
        of their inheritance while still allowing some flexibility for acts of
        charity. <br />
        The primary difference between Hibah and Atiyah lies in the giver’s
        condition at the time of the transfer. Hibah is given freely while the
        person is in good health and full control, making it an immediate and
        final transfer of ownership. Atiyah, however, is given when a person is
        elderly or suffering from a serious illness that may lead to death,
        making it legally scrutinized to prevent undue influence or unfair
        deprivation of heirs. <br />
        The Prophet (peace be upon him) emphasized the importance of justice in
        giving, stating, "Be afraid of Allah and be just to your children”
        <i>
          <a href="https://sunnah.com/bukhari:2587" target="blank">
            (Sahih al-Bukhari 2587)
          </a>
        </i>
        , which applies to both Hibah and Atiyah to ensure fairness in wealth
        distribution. <br />
        Thus, while both forms of giving are commendable, Hibah is a pure act of
        goodwill, whereas Atiyah must align with the broader framework of
        Islamic inheritance law to maintain justice among heirs.
        <br />
        Both Hibah and Atiyah are permanent transfer of ownership of wealth and
        are irrevocable unlike renting or leasing.
        <br />
        <br />
        <b>Revocability of Hibah and Atiyah Before Possession</b>
        <br />
        <br />
        In Islamic jurisprudence both Hibah and Atiyah are revocable before the
        recipient takes possession of the granted item. Many scholars agree that
        a Hibah or Atiyah does not necessarily have to be a specific, known
        object, rather it can include uncertain or undefined assets, such as an
        unborn calf in the womb of a cow. <br />
        However, for the transfer to be legally binding, the recipient must take
        physical possession of the granted asset. Until then, the donor retains
        the right to revoke the gift. <br />
        This principle is evidenced in the hadith of Abu Bakr (may Allah be
        pleased with him), who had granted a date palm tree to his daughter
        Aisha (may Allah be pleased with her) but later revoked it before she
        took possession .
        <br /> This ruling aligns with the general principle that a gift, to be
        irrevocable, requires full transfer of control. <br />
        Furthermore, if the donor or recipient passes away before possession is
        taken, the transfer is not automatically completed. Instead, the wealth
        reverts to the estate of the deceased, and it is left to the discretion
        of the heirs whether to honor the grant or revoke it. This ensures that
        rightful heirs are not deprived of their inheritance due to uncompleted
        transfers. The revocability of Hibah and Atiyah before possession
        safeguards fairness and prevents disputes over property that was never
        fully transferred to the recipient.
        <br />
        <br />
        <b>Father’s Right to Revoke</b>
        <br />
        <br />
        In Islam, once a Hibah or Atiyah has been accepted and the recipient has
        taken possession of it, it becomes irrevocable, except in the case of a
        father reclaiming what he gave to his child.
        <br /> The Prophet Muhammad (peace be upon him), made this distinction
        clear, stating: “It is not permissible for a person to take back what he
        has given as a gift, except a father regarding what he gives to his
        child. The one who takes back his gift is like a dog that vomits and
        then returns to eat its vomit.”{" "}
        <i>
          <a href="https://sunnah.com/tirmidhi:1298" target="blank">
            {" "}
            (Jami’ at-Tirmidhi 1298)
          </a>
        </i>
        .
        <br />
        This exception acknowledges the unique role of a father, who is
        responsible for the upbringing and financial well-being of his children,
        and thus may revoke a gift if needed for their benefit. However, for all
        others, taking back a gift after possession has been transferred is
        considered blameworthy and dishonorable. This ruling ensures that
        transactions remain fair and that once ownership is established, it
        cannot be arbitrarily reversed, maintaining justice and trust in
        financial dealings.
        <br />
        <br />
        <b>
          Hibah and Atiyah Can Be Given in All Permissible Things That Exist
        </b>
        <br />
        <br />
        In Islamic jurisprudence, Hibah (lifetime grant) and Atiyah (donation in
        old age or fatal illness) can be made in all things that are permissible
        to buy and sell under Shariah. This means that any lawful asset whether
        it is money, property, livestock, or other valuable possessions can be
        granted as a gift, provided it is something that is considered valid for
        trade and ownership in Islam. The fundamental principle governing this
        is that the asset must be halal (permissible) and free from elements of
        haram (forbidden matters), such as interest (riba), gambling (maysir),
        and uncertainty (gharar). Scholars emphasize that if something is
        permissible to be exchanged in a contract of sale, it is likewise
        permissible to be given as a Hibah or Atiyah, as both involve the lawful
        transfer of ownership.
        <br />A key condition for Hibah and Atiyah is that the item being
        granted must be present and in existence at the time of transfer. One
        cannot gift something that is non-existent or uncertain, such as a
        future harvest that has not yet grown or an unborn animal that may not
        survive. <br />
        The Prophet Muhammad (peace be upon him), forbade the selling of "the
        unborn offspring in the womb" , as it involves uncertainty and
        speculation. Since Hibah and Atiyah follow the same principles of valid
        transactions in Islam, they must also adhere to this requirement.
        However, scholars note that if an asset is uncertain but still legally
        recognized such as the calf inside a pregnant cow it can be gifted
        because its existence is linked to an identifiable, present entity.
        <br />
        An example of this principle is the gifting of a dog. In general, Islam
        prohibits selling or gifting a dog without necessity. However, it is
        permissible to give a dog as a Hibah or Atiyah if it is intended for a
        lawful purpose, such as hunting, herding, or guarding property. <br />
        The Prophet Muhammad, peace be upon him, said, "Whoever keeps a dog, one
        Qirat of the reward of his good deeds is deducted daily, unless the dog
        is used for guarding a farm or cattle."
        <i>
          <a href="https://sunnah.com/bukhari:2322" target="blank">
            (Sahih al-Bukhari 2322)
          </a>
        </i>
        .
        <br />
        This hadith indicates that keeping a dog merely as a pet is discouraged,
        and gifting such a dog would not be permissible. On the other hand,
        gifting a trained hunting or guard dog is allowed, as it serves a
        beneficial and lawful purpose. This distinction further highlights the
        rule that Hibah and Atiyah must only involve items that are permissible
        and beneficial according to Islamic principles. <br />
        <br />
        <b>
          Hibah and Atiyah Given to children should be in Proportion to
          Inheritance Shares
        </b>
        <br />
        <br />
        In Islam, the distribution of Hibah and Atiyah among children should
        follow the same proportional shares as inheritance, where a son receives
        twice the share of a daughter. <br />
        This ruling is derived from the hadith of Nu'man bin Bashir, who
        narrated that his father gave him a gift and then took him to the
        Prophet Muhammad (peace be upon him), to witness it. The Prophet asked
        if he had given the same to all his children, and when he replied that
        he had not, the Prophet instructed him to be just among his children.
        <br />
        Scholars have interpreted this to mean that while fairness is required,
        fairness does not necessarily mean equality but rather justice in
        accordance with Islamic inheritance laws. Just as a son's financial
        responsibilities in Islam are greater than a daughter's, he is entitled
        to a larger share in inheritance and accordingly in Hibah and Atiyah.
        This ensures balance in financial responsibilities within the family and
        aligns voluntary wealth transfers with the divine wisdom of inheritance
        laws.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        In today’s lesson, we explored the concepts of Hibah (lifetime grant)
        and Atiyah (donation given in old age or during a fatal illness) within
        Islamic jurisprudence. We learned that while both forms involve the
        voluntary transfer of ownership, Hibah is unrestricted and done during
        good health, whereas Atiyah is subject to inheritance laws and cannot
        exceed one-third of the estate without heirs’ approval. We also examined
        key rulings such as the requirement of possession for the gift to be
        binding, the father’s exclusive right to revoke a gift to his child, and
        the importance of giving in proportion to inheritance shares among
        children. The lesson highlighted the balance Islam strikes between
        generosity, justice, and the protection of rightful heirs.
      </p>

      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />

      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 1"
        badgeImg="./achieve.png"
        heading="Shariah Sentinel"
        subtitle="Graduate - Islamic Jurispudence on Finance"
        mainImg="./shariahsentinel.png"
        description="Granted to the Initiate who has established their foundation in the sources (Uṣūl) and basic rulings of financial transactions, where ethics meet enterprise."
        buttonText="Continue!"
      />
    </div>
  );
}

export default IslamicTradePage25;
